<script setup lang="ts">
import { useField } from 'vee-validate';
import { computed, nextTick, onMounted, ref, watch, watchEffect } from 'vue';

import { useWangsVueConfig } from 'lib/plugins/WangsVue';
import { formatVowelSoundLabel } from 'lib/utils/textFormatter.util';

import Calendar from './BaseCalendar.vue';
import CalendarTimeZone from './BaseCalendarTimeZone.vue';
import { CalendarEmits, CalendarProps } from './Calendar.vue.d';

const props = withDefaults(defineProps<CalendarProps>(), {
  view: 'date',
  mode: 'single',
  showYear: true,
  showOptionalText: undefined,
  valueFormat: 'millis',
  hideOnRangeSelection: true,

  dateFormat: 'dd/mm/yy',
  stepHour: 1,
  stepMinute: 1,
  timeSeparator: ':',
});

const emit = defineEmits<CalendarEmits>();

onMounted(() => {
  if (props.dateValue != null) {
    calendarModel.value = convertMillisToDate(props.dateValue);
    field.value = calendarModel.value;
    calendarKey.value++;
  }

  if (props.epochTimeMillis != null) {
    calendarModel.value = convertMillisToDate(props.epochTimeMillis);
    field.value = calendarModel.value;
    calendarKey.value++;
  }
});

type CalendarFieldValue =
  | typeof props.modelValue
  | typeof props.epochTimeMillis;

const userStorage = JSON.parse(localStorage.getItem('user') || '{}');
const userTimeZone: string | undefined = userStorage?.generalSetting?.timezone;

const { locale } = useWangsVueConfig('Calendar');

const calendarModel = ref<typeof props.modelValue>();
const calendarKey = ref<number>(0);

const { value: field, errorMessage } = props.useValidator
  ? useField<CalendarFieldValue>(props.fieldName ?? 'date', (value) => {
      return setValidatorMessage(value);
    })
  : {
      value: computed({
        get: () => {
          return props.modelValue;
        },

        set: (value: Date | Date[]) => {
          emit('update:modelValue', value);
        },
      }),
    };

const invalidInput = computed(
  () => props.invalid || ('errorMessage' in field && !!field.errorMessage),
);

const hourFormat = computed(() => {
  const userTimeFormat = userStorage?.generalSetting?.timeFormat;

  if (props.hourFormat) {
    return props.hourFormat;
  }
  if (userTimeFormat?.toLowerCase()?.includes('pm')) {
    return '12';
  }
  return '24';
});

const setValidatorMessage = async (
  value: CalendarFieldValue,
): Promise<boolean | string> => {
  await nextTick();

  if (typeof props.validatorMessage === 'string' && props.invalid) {
    return props.validatorMessage;
  } else if (
    typeof props.validatorMessage !== 'string' &&
    !value &&
    props.mandatory
  ) {
    return props.validatorMessage?.empty ?? getDefaultEmptyInvalidText();
  } else if (!value && props.mandatory) {
    return props.customValidation?.empty ?? getDefaultEmptyInvalidText();
  }

  return true;
};

const getDefaultEmptyInvalidText = (): string => {
  return locale.emptyInvalidText
    .replace('{label}', props.label)
    .replace(
      '{formattedLabel}',
      formatVowelSoundLabel(props.label)?.toLowerCase(),
    );
};

const onUpdateModelValue = (
  format: typeof props.valueFormat,
  e: typeof props.modelValue | typeof props.epochTimeMillis,
): void => {
  if (
    (format === props.valueFormat && props.useValidator) ||
    (!props.useValidator && format === 'date') // Ensure update:modelValue not emits number epoch time millis
  ) {
    field.value = e;
  }

  // Previously, when Calendar used with validation, the v-model will not works, this if block is to fix this issue
  if (format === 'date' && props.useValidator) {
    emit('update:modelValue', e as typeof props.modelValue);
  }

  if (format === 'millis') {
    emit('update:epochTimeMillis', e as typeof props.epochTimeMillis);
  }
};

const convertMillisToDate = (value: CalendarFieldValue): Date | Date[] => {
  return Array.isArray(value)
    ? value.map((millis) => (millis == null ? null : new Date(millis)))
    : (new Date(value) as typeof props.modelValue);
};

watch(
  () => props.dateValue,
  () => {
    calendarModel.value = convertMillisToDate(props.dateValue);
    if (props.useValidator) field.value = calendarModel.value;
    calendarKey.value++;
  },
  {
    once: true,
  },
);

watch(
  () => props.epochTimeMillis,
  () => {
    calendarModel.value = convertMillisToDate(props.epochTimeMillis);
    if (props.useValidator) field.value = calendarModel.value;
    calendarKey.value++;
  },
);

watch(
  () => props.modelValue,
  () => {
    calendarModel.value = props.modelValue;
    if (props.useValidator) field.value = calendarModel.value;
  },
);

watchEffect(() => {
  calendarModel.value = ((): Date | Date[] => {
    if (props.valueFormat === 'date')
      return field.value as typeof props.modelValue;

    return field.value
      ? convertMillisToDate(field.value)
      : (field.value as typeof props.modelValue);
  })();
});
</script>

<template>
  <CalendarTimeZone
    :key="calendarKey + 1"
    v-if="userTimeZone"
    v-bind="props"
    :error-message="errorMessage"
    :hour-format
    :invalid="invalidInput"
    :model-value="calendarModel"
    :selection-mode="selectionMode || mode"
    @update:epoch-time-millis="onUpdateModelValue('millis', $event)"
    @update:model-value="onUpdateModelValue('date', $event)"
  >
    <template #default="{ onClick }">
      <slot :on-click="onClick" />
    </template>

    <template #addon-overlay>
      <slot name="addon-overlay" />
    </template>
  </CalendarTimeZone>

  <Calendar
    :key="calendarKey"
    v-else
    v-bind="props"
    :error-message="errorMessage"
    :hour-format
    :invalid="invalidInput"
    :model-value="calendarModel"
    :selection-mode="selectionMode || mode"
    @update:epoch-time-millis="onUpdateModelValue('millis', $event)"
    @update:model-value="onUpdateModelValue('date', $event)"
  >
    <template #default="{ onClick }">
      <slot :on-click="onClick" />
    </template>

    <template #addon-overlay>
      <slot name="addon-overlay" />
    </template>
  </Calendar>
</template>
